import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BarChart, Bar, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, CheckCircle2, CircleAlert, ClipboardList, Download, ExternalLink, FileWarning, Filter, FolderOpen, LogOut, Menu, RefreshCw, Search, Settings2, SlidersHorizontal, Building2, UserCheck, WalletCards, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const palette = ["#0EA5E9", "#F97316", "#10B981", "#8B5CF6", "#F59E0B", "#EC4899", "#14B8A6", "#6366F1"];
const money = (value) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value);
const moneyCompact = (value) => `Rp ${new Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 2 }).format(value)}`;
const numberCompact = (value) => new Intl.NumberFormat("id-ID").format(value);

const STATUS_STYLE = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "selesai") return "done";
  if (s.includes("belum")) return "open";
  return "review";
};

const RISK_PRESETS = [
  { value: 0, label: "Semua nilai" },
  { value: 100_000_000, label: "≥ Rp 100 jt" },
  { value: 500_000_000, label: "≥ Rp 500 jt" },
  { value: 1_000_000_000, label: "≥ Rp 1 M" },
  { value: 5_000_000_000, label: "≥ Rp 5 M" },
];

const TABS = [
  { key: "kerugian", label: "Temuan Kerugian", endpoint: "kerugian-data", caption: "PT dengan temuan nilai kerugian" },
  { key: "masalah", label: "Masalah Non-Kerugian", endpoint: "masalah-data", caption: "Kasus tanpa nominal kerugian" },
  { key: "folders", label: "Folder Dokumen", endpoint: "folders", caption: "Arsip Google Drive per PT" },
];

function App() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("kerugian");
  const [data, setData] = useState({ kerugian: null, masalah: null, folders: null });
  const [loading, setLoading] = useState({ kerugian: true, masalah: true, folders: true });
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ tahun: "Semua tahun", pt: "Semua PT", status: "Semua status", query: "" });
  const [folderQuery, setFolderQuery] = useState("");
  const [activeDetail, setActiveDetail] = useState(null);
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [autoUpdatedAt, setAutoUpdatedAt] = useState(null);
  const [riskThreshold, setRiskThreshold] = useState(500_000_000);
  const [onlyRisky, setOnlyRisky] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reportMenuOpen, setReportMenuOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadTab = async (tabKey, refresh = false) => {
    const meta = TABS.find((t) => t.key === tabKey);
    try {
      if (refresh) setRefreshing(true);
      setLoading((s) => ({ ...s, [tabKey]: true }));
      const response = await axios.get(`${API}/${meta.endpoint}`, { params: refresh ? { refresh: true } : {} });
      setData((s) => ({ ...s, [tabKey]: response.data }));
      setError("");
    } catch (e) {
      setError("Data belum dapat dimuat. Silakan segarkan atau coba lagi.");
    } finally {
      setLoading((s) => ({ ...s, [tabKey]: false }));
      setRefreshing(false);
    }
  };

  const loadAllTabs = async (silent = false) => {
    const meta = TABS;
    const results = await Promise.all(meta.map((m) => axios.get(`${API}/${m.endpoint}`).then((r) => [m.key, r.data]).catch(() => [m.key, null])));
    setData((s) => {
      const next = { ...s };
      for (const [k, v] of results) { if (v) next[k] = v; }
      return next;
    });
    if (silent) setAutoUpdatedAt(new Date().toISOString());
  };

  useEffect(() => {
    loadTab("kerugian");
    loadTab("masalah");
    loadTab("folders");
  }, []);
  useEffect(() => { setFilters({ tahun: "Semua tahun", pt: "Semua PT", status: "Semua status", query: "" }); setActiveDetail(null); }, [tab]);

  // Poll schedule-info every 5 minutes. When cache_fetched_at changes, silently reload all tabs.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/schedule-info`);
        if (cancelled) return;
        const previousFetched = scheduleInfo?.cache_fetched_at;
        setScheduleInfo(res.data);
        if (previousFetched && res.data.cache_fetched_at && res.data.cache_fetched_at !== previousFetched) {
          loadAllTabs(true);
        }
      } catch (_) { /* keep quiet */ }
    };
    poll();
    const id = setInterval(poll, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleInfo?.cache_fetched_at]);

  const downloadReport = async (source, anonymize) => {
    setDownloading(true);
    setReportMenuOpen(false);
    try {
      const response = await axios.get(`${API}/report/pdf`, { params: { source, anonymize }, responseType: "blob" });
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match?.[1] || `Summary-KIPK-${source}-${anonymize ? "ringkas" : "detail"}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("Gagal mengunduh laporan PDF. Coba lagi beberapa saat.");
    } finally {
      setDownloading(false);
    }
  };

  const isKerugian = tab === "kerugian";
  const isMasalah = tab === "masalah";
  const isFolders = tab === "folders";
  const active = data[tab];
  const currentTabMeta = TABS.find((t) => t.key === tab);
  const lastUpdated = active?.fetched_at ? new Date(active.fetched_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Jakarta" }) : "—";

  if (loading[tab] && !active) return <div className="loading-screen" data-testid="dashboard-loading">Memuat data KIPK…</div>;

  return (
    <div className={`app-shell ${sidebarOpen ? "sidebar-open" : ""}`}>
      {sidebarOpen && <div className="sidebar-backdrop" data-testid="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
      <aside className="sidebar" data-testid="sidebar">
        <div className="brand-mark">
          <Link to="/padma" className="brand-icon brand-logo padma-home-link" aria-label="Kembali ke Akses Layanan PADMA" title="Akses Layanan PADMA" data-testid="dashboard-padma-home-link"><img src="/logo-padma-transparent.png" alt="Logo PADMA" data-testid="dashboard-padma-logo" /></Link>
          <div><strong>KIPK LLDIKTI VII</strong><span>OPERATIONS DESK</span></div>
          <button className="sidebar-close" data-testid="sidebar-close" aria-label="Tutup menu" onClick={() => setSidebarOpen(false)}><X size={18} /></button>
        </div>
        <nav>
          <div className="nav-caption">HALAMAN</div>
          <button className="nav-item nav-active" data-testid="nav-pusat-kendali">
            <WalletCards size={16} />
            <div className="nav-item-body"><span>Pusat Kendali Masalah KIPK</span></div>
          </button>
          {user?.role === "master" && <Link to="/kipk/manajemen-akun" className="nav-item" data-testid="nav-manajemen-akun">
            <Settings2 size={16} />
            <div className="nav-item-body"><span>Manajemen Akun</span></div>
          </Link>}
          <Link to="/kipk/manajemen-pt" className="nav-item" data-testid="nav-manajemen-pt">
            <UserCheck size={16} />
            <div className="nav-item-body"><span>Manajemen PT</span></div>
          </Link>
          <Link to="/kipk/manajemen-task" className="nav-item" data-testid="nav-manajemen-task">
            <ClipboardList size={16} />
            <div className="nav-item-body"><span>Manajemen Task</span></div>
          </Link>
          <Link to="/kipk/rekap-pt" className="nav-item" data-testid="nav-rekap-pt">
            <Building2 size={16} />
            <div className="nav-item-body"><span>Rekapitulasi PT</span></div>
          </Link>
        </nav>
        <div className="sidebar-footer">
          <div className="acct-me"><CheckCircle2 size={14} /><div><strong data-testid="me-display-name">{user?.display_name}</strong><small data-testid="me-role">Peran: {user?.role === "master" ? "Super Admin" : "Admin"}</small></div></div>
          <button className="download-button ghost logout-btn" data-testid="kipk-logout" onClick={async () => { await logout(); navigate("/login", { replace: true }); }}><LogOut size={14} /> Keluar</button>
          <span className="status-dot" /> Data terhubung<br />
          <small data-testid="last-updated-footer">Update: {lastUpdated}{active?.cached ? " (cache)" : ""}</small>
          {scheduleInfo?.next_scheduled_run && (
            <>
              <br /><small data-testid="next-refresh-footer" title="Refresh terjadwal harian pukul 00:01 WIB">
                Auto-refresh: {new Date(scheduleInfo.next_scheduled_run).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short", timeZone: "Asia/Jakarta" })} WIB
              </small>
            </>
          )}
        </div>
      </aside>

      <main className="main-content">
        {autoUpdatedAt && (
          <div className="info-banner" data-testid="auto-update-banner">
            <CheckCircle2 size={16} /> Data otomatis diperbarui pada {new Date(autoUpdatedAt).toLocaleString("id-ID", { timeStyle: "short", dateStyle: "short", timeZone: "Asia/Jakarta" })} WIB.
            <button className="banner-close" data-testid="dismiss-auto-update" onClick={() => setAutoUpdatedAt(null)} aria-label="Tutup pemberitahuan"><X size={14} /></button>
          </div>
        )}
        <header className="topbar">
          <button className="icon-button menu-button" data-testid="sidebar-open-button" aria-label="Buka menu" onClick={() => setSidebarOpen(true)}><Menu size={18} /></button>
          <div className="topbar-title">
            <div className="breadcrumb" data-testid="breadcrumb-label">PUSAT KENDALI MASALAH KIPK LLDIKTI WILAYAH VII</div>
            <h1 data-testid="dashboard-title">
              {isKerugian ? "Masalah dengan Temuan Kerugian" : isMasalah ? "Ikhtisar masalah non-kerugian KIPK" : "Folder dokumen per perguruan tinggi"}
            </h1>
            <p className="subtitle" data-testid="dashboard-subtitle">{currentTabMeta.caption}.</p>
          </div>
          <div className="header-meta">
            <span data-testid="last-updated-label">Diperbarui · {lastUpdated}</span>
            <div className="report-menu-wrap">
              <button className="download-button" data-testid="download-report-button" aria-label="Unduh Summary Report" aria-haspopup="menu" aria-expanded={reportMenuOpen} onClick={() => setReportMenuOpen((v) => !v)} disabled={downloading}>
                <Download size={15} className={downloading ? "spin" : ""} />
                <span className="download-label">{downloading ? "Menyiapkan…" : "Unduh Report"}</span>
              </button>
              {reportMenuOpen && (
                <>
                  <div className="menu-backdrop" onClick={() => setReportMenuOpen(false)} />
                  <div className="report-menu" data-testid="report-menu" role="menu">
                    <div className="report-menu-title">Summary Report PDF</div>
                    <div className="report-menu-sub">Data 3 tahun terakhir · rincian per tahun · grafik</div>

                    <div className="report-menu-group">TEMUAN KERUGIAN</div>
                    <button className="report-menu-item" data-testid="download-kerugian-detail" role="menuitem" onClick={() => downloadReport("kerugian", false)}>
                      <strong>Detail — dengan nama PT</strong>
                      <small>Rincian kasus lengkap per perguruan tinggi</small>
                    </button>
                    <button className="report-menu-item" data-testid="download-kerugian-ringkas" role="menuitem" onClick={() => downloadReport("kerugian", true)}>
                      <strong>Ringkas — tanpa nama PT</strong>
                      <small>Rekap jumlah PT selesai vs dalam proses</small>
                    </button>

                    <div className="report-menu-group">MASALAH NON-KERUGIAN</div>
                    <button className="report-menu-item" data-testid="download-masalah-detail" role="menuitem" onClick={() => downloadReport("masalah", false)}>
                      <strong>Detail — dengan nama PT</strong>
                      <small>Rincian kasus lengkap per PT</small>
                    </button>
                    <button className="report-menu-item" data-testid="download-masalah-ringkas" role="menuitem" onClick={() => downloadReport("masalah", true)}>
                      <strong>Ringkas — tanpa nama PT</strong>
                      <small>Rekap jumlah PT selesai vs dalam proses</small>
                    </button>

                    <a className="report-menu-item link" href="/ringkas" target="_blank" rel="noopener noreferrer" data-testid="open-public-summary">
                      <strong>Halaman ringkasan publik →</strong>
                      <small>Bisa dibagikan; tanpa nama PT & tanpa filter</small>
                    </a>
                  </div>
                </>
              )}
            </div>
            <button className="icon-button" data-testid="refresh-button" aria-label="Segarkan data" onClick={() => loadTab(tab, true)} disabled={refreshing}>
              <RefreshCw size={17} className={refreshing ? "spin" : ""} />
            </button>
            <button className="icon-button" data-testid="filter-toggle-button" aria-label="Buka filter" onClick={() => document.querySelector('[data-testid="global-filter-rail"], [data-testid="folder-search-rail"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}>
              <SlidersHorizontal size={18} />
            </button>
          </div>
        </header>

        <div className="tab-strip" data-testid="tab-strip">
          {TABS.map((t) => (
            <button key={t.key} className={`tab-button ${tab === t.key ? "active" : ""}`} data-testid={`tab-button-${t.key}`} onClick={() => setTab(t.key)}>
              {t.label}
              <span className="tab-count">
                {t.key === "folders" ? (data.folders?.total ?? "…") : (data[t.key]?.totals?.total_kasus ?? "…")}
              </span>
            </button>
          ))}
        </div>

        {error && <div className="error-banner" data-testid="data-error-alert"><CircleAlert size={17} /> {error}</div>}

        {isFolders ? (
          <FoldersView payload={data.folders} query={folderQuery} setQuery={setFolderQuery} />
        ) : (
          <IssuesView
            isKerugian={isKerugian}
            payload={active}
            filters={filters}
            setFilters={setFilters}
            setActiveDetail={setActiveDetail}
            riskThreshold={riskThreshold}
            setRiskThreshold={setRiskThreshold}
            onlyRisky={onlyRisky}
            setOnlyRisky={setOnlyRisky}
          />
        )}
      </main>

      {activeDetail && (
        <div className="drawer-backdrop" data-testid="detail-drawer-backdrop" onClick={() => setActiveDetail(null)}>
          <aside className="detail-drawer" data-testid="issue-detail-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-top">
              <div>
                <div className="eyebrow">DETAIL KASUS #{String(activeDetail.id).padStart(2, "0")}</div>
                <h2>{activeDetail.nama_pt}</h2>
              </div>
              <button className="icon-button" data-testid="close-detail-drawer-button" aria-label="Tutup detail" onClick={() => setActiveDetail(null)}><X size={18} /></button>
            </div>
            <div className="drawer-status">
              <span className={`status-badge ${STATUS_STYLE(activeDetail.penyelesaian)}`}>{activeDetail.penyelesaian || "—"}</span>
              <span className="mono">Tahun {activeDetail.tahun}</span>
            </div>
            {activeDetail.folder_url && (
              <a className="drawer-cta" href={activeDetail.folder_url} target="_blank" rel="noopener noreferrer" data-testid="drawer-folder-link">
                <FolderOpen size={16} /> Buka folder dokumen Drive <ExternalLink size={13} />
              </a>
            )}
            <dl>
              <dt>Masalah</dt>
              <dd>
                {activeDetail.masalah}
                {activeDetail.masalah_url && (
                  <>
                    {" "}
                    <a className="inline-link" href={activeDetail.masalah_url} target="_blank" rel="noopener noreferrer" data-testid="drawer-masalah-link">
                      <ExternalLink size={12} /> lihat dokumen
                    </a>
                  </>
                )}
              </dd>
              {activeDetail.instansi && (<><dt>Instansi Handling</dt><dd>{activeDetail.instansi}</dd></>)}
              <dt>Hasil / Kesimpulan</dt><dd>{activeDetail.hasil}</dd>
              {activeDetail.total_kerugian !== undefined && (<>
                <dt>Total kerugian</dt><dd className="drawer-money">{money(activeDetail.total_kerugian || 0)}</dd>
                <dt>Sudah dikembalikan</dt><dd>{money(activeDetail.proses_pengembalian || 0)}</dd>
                <dt>Belum dikembalikan</dt><dd>{money(activeDetail.belum_dikembalikan || 0)}</dd>
              </>)}
            </dl>
          </aside>
        </div>
      )}
    </div>
  );
}

function IssuesView({ isKerugian, payload, filters, setFilters, setActiveDetail, riskThreshold, setRiskThreshold, onlyRisky, setOnlyRisky }) {
  const records = payload?.records || [];
  const totals = payload?.totals;

  const options = useMemo(() => ({
    years: [...new Set(records.map((r) => r.tahun).filter(Boolean))].sort(),
    pts: [...new Set(records.map((r) => r.nama_pt).filter(Boolean))].sort(),
    statuses: [...new Set(records.map((r) => r.penyelesaian).filter(Boolean))].sort(),
  }), [records]);

  const filtered = useMemo(() => records.filter((r) => {
    const query = filters.query.toLowerCase().trim();
    const isRisky = isKerugian && riskThreshold > 0 && (r.belum_dikembalikan || 0) >= riskThreshold;
    return (filters.tahun === "Semua tahun" || String(r.tahun) === filters.tahun)
      && (filters.pt === "Semua PT" || r.nama_pt === filters.pt)
      && (filters.status === "Semua status" || r.penyelesaian === filters.status)
      && (!query || `${r.nama_pt} ${r.masalah} ${r.hasil}`.toLowerCase().includes(query))
      && (!onlyRisky || isRisky);
  }), [records, filters, isKerugian, riskThreshold, onlyRisky]);

  const isRowRisky = (r) => isKerugian && riskThreshold > 0 && (r.belum_dikembalikan || 0) >= riskThreshold;

  const riskyCount = useMemo(() => filtered.filter((r) => isRowRisky(r)).length, [filtered, riskThreshold, isKerugian]);
  const riskyBelum = useMemo(() => filtered.filter((r) => isRowRisky(r)).reduce((s, r) => s + (r.belum_dikembalikan || 0), 0), [filtered, riskThreshold, isKerugian]);

  const summary = useMemo(() => {
    if (!isKerugian) {
      const selesai = filtered.filter((r) => (r.penyelesaian || "").toLowerCase() === "selesai").length;
      return { total: filtered.length, selesai, lain: filtered.length - selesai };
    }
    const kerugian = filtered.reduce((s, r) => s + r.total_kerugian, 0);
    const proses = filtered.reduce((s, r) => s + r.proses_pengembalian, 0);
    const belum = filtered.reduce((s, r) => s + r.belum_dikembalikan, 0);
    return {
      total: filtered.length,
      kerugian, proses, belum,
      persen: kerugian > 0 ? Math.round((proses / kerugian) * 1000) / 10 : 0,
      belumPersen: kerugian > 0 ? Math.round((belum / kerugian) * 1000) / 10 : 0,
      selesai: filtered.filter((r) => (r.penyelesaian || "").toLowerCase() === "selesai").length,
    };
  }, [filtered, isKerugian]);

  const topPTData = useMemo(() => {
    const grouped = filtered.reduce((acc, r) => {
      if (!r.nama_pt) return acc;
      const bucket = acc[r.nama_pt] || { name: r.nama_pt, kasus: 0, kerugian: 0, dikembalikan: 0, belum: 0 };
      bucket.kasus += 1;
      if (isKerugian) {
        bucket.kerugian += r.total_kerugian;
        bucket.dikembalikan += r.proses_pengembalian;
        bucket.belum += r.belum_dikembalikan;
      }
      acc[r.nama_pt] = bucket;
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => (isKerugian ? b.kerugian - a.kerugian : b.kasus - a.kasus)).slice(0, 10);
  }, [filtered, isKerugian]);

  const statusData = useMemo(() => {
    const grouped = filtered.reduce((acc, r) => { acc[r.penyelesaian || "Tidak diketahui"] = (acc[r.penyelesaian || "Tidak diketahui"] || 0) + 1; return acc; }, {});
    return Object.entries(grouped).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered]);

  const yearData = useMemo(() => {
    const grouped = filtered.reduce((acc, r) => { acc[r.tahun] = (acc[r.tahun] || 0) + 1; return acc; }, {});
    return Object.entries(grouped).map(([year, value]) => ({ year: String(year), value })).sort((a, b) => a.year.localeCompare(b.year));
  }, [filtered]);

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));
  const reset = () => { setFilters({ tahun: "Semua tahun", pt: "Semua PT", status: "Semua status", query: "" }); setOnlyRisky(false); };

  return (
    <>
      <section className="filter-rail" data-testid="global-filter-rail">
        <div className="filter-label"><Filter size={15} /> FILTER DATA</div>
        <select data-testid="year-filter" value={filters.tahun} onChange={(e) => setFilter("tahun", e.target.value)}>
          <option>Semua tahun</option>
          {options.years.map((y) => <option key={y}>{y}</option>)}
        </select>
        <select data-testid="pt-filter" value={filters.pt} onChange={(e) => setFilter("pt", e.target.value)}>
          <option>Semua PT</option>
          {options.pts.map((p) => <option key={p}>{p}</option>)}
        </select>
        <select data-testid="status-filter" value={filters.status} onChange={(e) => setFilter("status", e.target.value)}>
          <option>Semua status</option>
          {options.statuses.map((s) => <option key={s}>{s}</option>)}
        </select>
        {isKerugian && (
          <>
            <select data-testid="risk-threshold-filter" value={riskThreshold} onChange={(e) => setRiskThreshold(Number(e.target.value))} title="Ambang batas tunggakan berisiko">
              {RISK_PRESETS.map((p) => <option key={p.value} value={p.value} label={`Risiko ${p.label}`} />)}
            </select>
            <button
              type="button"
              className={`chip-toggle ${onlyRisky ? "active" : ""}`}
              data-testid="only-risky-toggle"
              onClick={() => setOnlyRisky((v) => !v)}
              disabled={riskThreshold === 0}
              title={riskThreshold === 0 ? "Pilih ambang batas terlebih dahulu" : "Hanya tampilkan kasus berisiko"}
            >
              <AlertTriangle size={13} /> Hanya berisiko
            </button>
          </>
        )}
        <div className="search-wrap">
          <Search size={16} />
          <input data-testid="issue-search-input" value={filters.query} onChange={(e) => setFilter("query", e.target.value)} placeholder="Cari institusi, masalah, atau kesimpulan…" />
        </div>
        <button className="reset-button" data-testid="reset-filters-button" onClick={reset}><X size={14} /> Reset</button>
      </section>

      <section className="kpi-grid" aria-label="Ringkasan utama">
        {isKerugian ? (
          <>
            <Kpi label="Total kasus" value={numberCompact(summary.total)} helper={`${totals?.total_pt || 0} perguruan tinggi`} icon={<CircleAlert />} tone="blue" testid="total-cases-kpi" />
            <Kpi label="Sudah dikembalikan" value={moneyCompact(summary.proses)} helper={`${summary.persen}% dari nilai temuan`} icon={<CheckCircle2 />} tone="green" testid="total-returned-kpi" />
            <Kpi label="Belum dikembalikan" value={moneyCompact(summary.belum)} helper={`${summary.belumPersen}% masih tertahan`} icon={<CircleAlert />} tone="red" testid="total-outstanding-kpi" />
            <Kpi
              label={`Kasus berisiko ${riskThreshold > 0 ? `≥ ${moneyCompact(riskThreshold)}` : ""}`}
              value={numberCompact(riskyCount)}
              helper={riskyCount ? `${moneyCompact(riskyBelum)} total tunggakan` : "Tidak ada kasus berisiko"}
              icon={<AlertTriangle />}
              tone="amber"
              testid="risky-cases-kpi"
            />
          </>
        ) : (
          <>
            <Kpi label="Total kasus" value={numberCompact(summary.total)} helper={`${totals?.total_pt || 0} perguruan tinggi`} icon={<FileWarning />} tone="blue" testid="total-cases-kpi" />
            <Kpi label="Kasus selesai" value={numberCompact(summary.selesai)} helper={`${summary.total ? Math.round(summary.selesai / summary.total * 100) : 0}% dari total`} icon={<CheckCircle2 />} tone="green" testid="total-completed-kpi" />
            <Kpi label="Butuh tindak lanjut" value={numberCompact(summary.lain)} helper="Status belum selesai / evaluasi" icon={<CircleAlert />} tone="red" testid="total-pending-kpi" />
            <Kpi label="Folder dokumen" value={numberCompact(totals?.total_folder || 0)} helper="Arsip Drive terhubung" icon={<FolderOpen />} tone="amber" testid="total-folders-kpi" />
          </>
        )}
      </section>

      <section className="analysis-grid">
        <Panel title={isKerugian ? "Top PT dengan nilai temuan terbesar" : "PT dengan jumlah kasus terbanyak"} eyebrow={isKerugian ? "RANKING NILAI" : "RANKING KASUS"} className="wide" testid="pt-ranking-panel">
          <ResponsiveContainer width="100%" height={340}>
            <BarChart layout="vertical" data={topPTData} margin={{ left: 10, right: 24, top: 8, bottom: 8 }} onClick={(state) => state?.activePayload?.[0] && setFilter("pt", state.activePayload[0].payload.name)}>
              <CartesianGrid stroke="#D5D2CA" horizontal={false} />
              <XAxis type="number" tickFormatter={isKerugian ? moneyCompact : undefined} tick={{ fill: "#59616A", fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={200} tick={{ fill: "#59616A", fontSize: 11 }} />
              <Tooltip formatter={(value, key) => isKerugian ? [money(value), key === "dikembalikan" ? "Sudah dikembalikan" : "Belum dikembalikan"] : [`${value} kasus`, "Jumlah"]} />
              {isKerugian ? (
                <>
                  <Legend formatter={(name) => name === "dikembalikan" ? "Sudah dikembalikan" : "Belum dikembalikan"} />
                  <Bar dataKey="dikembalikan" stackId="a" fill="#10B981" />
                  <Bar dataKey="belum" stackId="a" fill="#C4302B" radius={[0, 6, 6, 0]} />
                </>
              ) : (
                <Bar dataKey="kasus" radius={[0, 3, 3, 0]}>
                  {topPTData.map((entry, index) => <Cell key={entry.name} fill={palette[index % palette.length]} />)}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Distribusi status" eyebrow="STATUS PENYELESAIAN" testid="status-distribution-panel">
          <ResponsiveContainer width="100%" height={340}>
            <BarChart layout="vertical" data={statusData} margin={{ left: 10, right: 18, top: 8, bottom: 8 }} onClick={(state) => state?.activePayload?.[0] && setFilter("status", state.activePayload[0].payload.name)}>
              <CartesianGrid stroke="#D5D2CA" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fill: "#59616A", fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={140} tick={{ fill: "#59616A", fontSize: 11 }} />
              <Tooltip formatter={(value) => [`${value} kasus`, "Jumlah"]} />
              <Bar dataKey="value" radius={[0, 3, 3, 0]}>
                {statusData.map((entry, index) => <Cell key={entry.name} fill={palette[index % palette.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Sebaran per tahun" eyebrow="TIMELINE KASUS" testid="year-distribution-panel">
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={yearData} margin={{ top: 12, right: 16, left: -10, bottom: 6 }} onClick={(state) => state?.activePayload?.[0] && setFilter("tahun", state.activePayload[0].payload.year)}>
              <CartesianGrid stroke="#D5D2CA" vertical={false} />
              <XAxis dataKey="year" tick={{ fill: "#59616A", fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#59616A", fontSize: 12 }} />
              <Tooltip formatter={(value) => [`${value} kasus`, "Jumlah"]} />
              <Bar dataKey="value" fill="#0EA5E9" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </section>

      <section className="table-section">
        <div className="section-heading">
          <div>
            <div className="eyebrow">CATATAN DETAIL</div>
            <h2 data-testid="detail-table-title">{isKerugian ? "Daftar temuan kerugian" : "Daftar masalah non-kerugian"}</h2>
          </div>
          <span className="result-count" data-testid="filtered-result-count">{filtered.length} dari {records.length} kasus</span>
        </div>
        <div className="table-wrap">
          <table data-testid="issues-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Tahun</th>
                <th>Perguruan Tinggi</th>
                <th>Masalah</th>
                {isKerugian && <th>Instansi</th>}
                <th>Hasil / Kesimpulan</th>
                {isKerugian && <th>Nilai temuan</th>}
                {isKerugian && <th>Belum</th>}
                <th>Status</th>
                <th>Folder</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const risky = isRowRisky(row);
                return (
                <tr data-testid={`issue-row-${row.id}`} key={row.id} className={risky ? "row-risky" : ""} onClick={() => setActiveDetail(row)}>
                  <td className="mono">
                    #{String(row.id).padStart(2, "0")}
                    {risky && <AlertTriangle size={12} className="risk-icon" data-testid={`risk-flag-${row.id}`} aria-label="Kasus berisiko tinggi" />}
                  </td>
                  <td className="mono">{row.tahun}</td>
                  <td><strong>{row.nama_pt}</strong></td>
                  <td>
                    {row.masalah}
                    {row.masalah_url && (
                      <>
                        {" "}
                        <a className="inline-link" href={row.masalah_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`row-masalah-link-${row.id}`} aria-label={`Buka dokumen masalah ${row.nama_pt}`} title="Lihat dokumen">
                          <ExternalLink size={11} />
                        </a>
                      </>
                    )}
                  </td>
                  {isKerugian && <td>{row.instansi || "—"}</td>}
                  <td className="result-cell">{row.hasil}</td>
                  {isKerugian && <td className="money-cell">{row.total_kerugian ? money(row.total_kerugian) : "—"}</td>}
                  {isKerugian && <td className={`money-cell ${risky ? "money-risky" : ""}`}>{row.belum_dikembalikan ? money(row.belum_dikembalikan) : "—"}</td>}
                  <td><span className={`status-badge ${STATUS_STYLE(row.penyelesaian)}`}>{row.penyelesaian || "—"}</span></td>
                  <td>
                    {row.folder_url ? (
                      <a className="folder-chip" href={row.folder_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`row-folder-link-${row.id}`} title="Buka folder Drive">
                        <FolderOpen size={13} /> Buka
                      </a>
                    ) : <span className="muted-text">—</span>}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="empty-state" data-testid="no-results-message">Tidak ada kasus yang cocok. <button onClick={reset} data-testid="empty-reset-button">Hapus filter</button></div>}
        </div>
      </section>
    </>
  );
}

function FoldersView({ payload, query, setQuery }) {
  const folders = payload?.folders || [];
  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return folders;
    return folders.filter((f) => f.nama_pt.toLowerCase().includes(q));
  }, [folders, query]);

  return (
    <>
      <section className="filter-rail" data-testid="folder-search-rail">
        <div className="filter-label"><FolderOpen size={15} /> ARSIP DOKUMEN</div>
        <div className="search-wrap" style={{ flex: 1 }}>
          <Search size={16} />
          <input data-testid="folder-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari perguruan tinggi…" />
        </div>
        {query && <button className="reset-button" data-testid="reset-folder-search" onClick={() => setQuery("")}><X size={14} /> Reset</button>}
      </section>

      <section className="folder-grid" data-testid="folder-grid">
        {filtered.map((f) => (
          <a key={f.nama_pt} className="folder-card" href={f.folder_url} target="_blank" rel="noopener noreferrer" data-testid={`folder-card-${f.nama_pt.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="folder-card-icon"><FolderOpen size={22} /></div>
            <div className="folder-card-body">
              <div className="eyebrow">
                {f.sources.map((s) => s === "kerugian" ? "TEMUAN KERUGIAN" : "NON-KERUGIAN").join(" · ")}
                <span className="mono"> · {f.tahun_terakhir}</span>
              </div>
              <strong>{f.nama_pt}</strong>
              <small>{f.kasus} kasus tercatat</small>
            </div>
            <ExternalLink size={15} className="folder-card-arrow" />
          </a>
        ))}
        {filtered.length === 0 && <div className="empty-state" data-testid="folder-empty-state">Tidak ada folder yang cocok.</div>}
      </section>
    </>
  );
}

function Kpi({ label, value, helper, icon, tone, testid }) {
  return (
    <div className={`kpi-card ${tone}`} data-testid={testid}>
      <div className="kpi-top"><span>{label}</span><span className="kpi-icon">{icon}</span></div>
      <strong data-testid={`${testid}-value`}>{value}</strong>
      <small>{helper}</small>
    </div>
  );
}

function Panel({ title, eyebrow, children, className = "", testid }) {
  return (
    <div className={`panel ${className}`} data-testid={testid}>
      <div className="panel-heading">
        <div><div className="eyebrow">{eyebrow}</div><h2>{title}</h2></div>
        <span className="panel-mark">↗</span>
      </div>
      {children}
    </div>
  );
}

export default App;
